/**
 * SpeechRecognition.js - Gestion de la reconnaissance vocale
 * Implémentation complète de la Web Speech API
 */

import { SPEECH_COMMANDS, SPEECH_CONFIG } from "./constants.js";

export class SpeechRecognition {
  constructor(options = {}) {
    this.options = {
      onResult: options.onResult || (() => {}),
      onError: options.onError || (() => {}),
      onStart: options.onStart || (() => {}),
      onEnd: options.onEnd || (() => {}),
      onNoMatch: options.onNoMatch || (() => {}),
      ...options,
    };

    this.recognition = null;
    this.isActive = false;
    this.timeoutId = null;

    // Initialiser la reconnaissance
    this.initRecognition();
  }

  /**
   * Initialiser la reconnaissance vocale
   */
  initRecognition() {
    // Vérifier le support du navigateur
    if (!this.isSupported()) {
      console.error("Web Speech API non supportée");
      this.options.onError("not-supported");
      return;
    }

    // Créer l'instance de reconnaissance
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.recognition = new SpeechRecognitionAPI();

    // Configuration
    this.recognition.continuous = SPEECH_CONFIG.continuous;
    this.recognition.interimResults = SPEECH_CONFIG.interimResults;
    this.recognition.lang = SPEECH_CONFIG.language;

    // Événements
    this.setupEventListeners();
  }

  /**
   * Vérifier si la reconnaissance vocale est supportée
   */
  isSupported() {
    return "SpeechRecognition" in window || "webkitSpeechRecognition" in window;
  }

  /**
   * Configurer les événements de reconnaissance
   */
  setupEventListeners() {
    if (!this.recognition) return;

    this.recognition.onstart = () => {
      this.isActive = true;
      this.resetTimeout();
      this.options.onStart();
    };

    this.recognition.onresult = (event) => {
      const transcript = event.results[event.results.length - 1][0].transcript;

      // Analyser la commande
      const command = this.parseCommand(transcript);

      // Appeler le callback avec le transcript et la commande
      this.options.onResult(transcript, command);

      // Reset du timeout si commande reconnue
      if (command) {
        this.resetTimeout();
      } else {
        this.options.onNoMatch(transcript);
      }
    };

    this.recognition.onerror = (event) => {
      console.error("🎤 Erreur:", event.error);

      // Gérer différents types d'erreurs
      switch (event.error) {
        case "not-allowed":
        case "service-not-allowed":
          this.options.onError("permissions-denied");
          break;
        case "network":
          this.options.onError("network-error");
          break;
        case "no-speech":
          // Continuer l'écoute en cas de silence
          break;
        default:
          this.options.onError(event.error);
      }
    };

    this.recognition.onend = () => {
      // Redémarrer automatiquement si toujours actif
      if (this.isActive) {
        setTimeout(() => {
          if (this.isActive) {
            this.restart();
          }
        }, 500);
      }

      this.options.onEnd();
    };
  }

  /**
   * Analyser la commande vocale
   */
  parseCommand(transcript) {
    const text = transcript.toLowerCase().trim();

    // Parcourir toutes les commandes possibles
    for (const [status, keywords] of Object.entries(SPEECH_COMMANDS)) {
      for (const keyword of keywords) {
        if (text.includes(keyword.toLowerCase())) {
          return status;
        }
      }
    }

    return null;
  }

  /**
   * Démarrer la reconnaissance
   */
  start() {
    if (!this.recognition) {
      this.options.onError("not-initialized");
      return;
    }

    if (this.isActive) {
      return;
    }

    try {
      this.isActive = true;
      this.recognition.start();
    } catch (error) {
      console.error("❌ Erreur lors du démarrage:", error);
      this.isActive = false;
      this.options.onError("start-failed");
    }
  }

  /**
   * Arrêter la reconnaissance
   */
  stop() {
    this.isActive = false;
    this.clearTimeout();

    if (this.recognition) {
      try {
        this.recognition.stop();
      } catch (error) {
        console.error("❌ Erreur lors de l'arrêt:", error);
      }
    }
  }

  /**
   * Redémarrer la reconnaissance
   */
  restart() {
    if (!this.isActive) return;

    try {
      this.recognition.start();
    } catch (error) {
      console.error("❌ Erreur lors du redémarrage:", error);
      // Réessayer après un délai
      setTimeout(() => {
        if (this.isActive) {
          this.restart();
        }
      }, 1000);
    }
  }

  /**
   * Gérer le timeout de 30 secondes
   */
  resetTimeout() {
    this.clearTimeout();

    this.timeoutId = setTimeout(() => {
      this.stop();
      this.options.onError("timeout");
    }, SPEECH_CONFIG.timeout);
  }

  /**
   * Supprimer le timeout
   */
  clearTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  /**
   * Vérifier si la reconnaissance est active
   */
  isListening() {
    return this.isActive;
  }

  /**
   * Obtenir les commandes disponibles formatées
   */
  getAvailableCommands() {
    const commands = [];

    Object.entries(SPEECH_COMMANDS).forEach(([status, keywords]) => {
      commands.push({
        status: status,
        keywords: keywords,
        formatted: keywords.join(", "),
      });
    });

    return commands;
  }

  /**
   * Tester la reconnaissance avec un mot
   */
  testCommand(text) {
    return this.parseCommand(text);
  }

  /**
   * Nettoyer les ressources
   */
  destroy() {
    this.stop();
    this.recognition = null;
  }
}
